
// ABOUTME: Password strength indicator component for visual feedback
// ABOUTME: Shows password requirements and strength level with color coding

import React from 'react';
import { Progress } from '@/components/ui/progress';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  errors,
  strength
}) => {
  const getStrengthValue = () => {
    switch (strength) {
      case 'weak': return 25;
      case 'medium': return 75;
      case 'strong': return 100;
      default: return 0;
    }
  };

  const getStrengthColor = () => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-blue-500';
      case 'strong': return 'bg-green-500';
      default: return 'bg-gray-300';
    }
  };

  const getStrengthTextColor = () => {
    switch (strength) {
      case 'weak': return 'text-red-600';
      case 'medium': return 'text-blue-600';
      case 'strong': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const requirements = [
    { text: 'At least 8 characters', test: password.length >= 8 },
    { text: 'One uppercase letter', test: /[A-Z]/.test(password) },
    { text: 'One lowercase letter', test: /[a-z]/.test(password) },
    { text: 'One number', test: /\d/.test(password) },
    { text: 'One special character', test: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];

  if (!password) return null;

  return (
    <div className="mt-2 space-y-2 bg-white p-3 rounded border">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-700">Password strength:</span>
        <span className={`text-sm font-medium ${getStrengthTextColor()}`}>
          {strength.charAt(0).toUpperCase() + strength.slice(1)}
        </span>
      </div>
      
      <Progress 
        value={getStrengthValue()} 
        className={`h-2 ${getStrengthColor()}`}
      />
      
      <div className="space-y-1">
        {requirements.map((req, index) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            {req.test ? (
              <Check className="w-3 h-3 text-green-500" />
            ) : (
              <X className="w-3 h-3 text-red-500" />
            )}
            <span className={req.test ? 'text-green-600' : 'text-red-600'}>
              {req.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
